import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Progress } from './ui/progress';
import {
  Brain,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Edit,
  Pause,
  BarChart3,
  RefreshCw,
  AlertCircle,
  Eye,
} from 'lucide-react';

// Importar hooks y tipos
import { useModels } from '../hooks';

// Tipos de estado para TypeScript
type StatusType =
  | 'completed'
  | 'failed'
  | 'pending'
  | 'in-progress'
  | 'active'
  | 'draft'
  | 'paused'
  | 'training'
  | 'deployed';

export default function ModelsTab() {
  // Estados locales para formularios y UI
  const [selectedModel, setSelectedModel] = useState<any>(null);

  // Hooks para datos
  const models = useModels();

  const getStatusBadge = (status: StatusType) => {
    const statusConfig = {
      completed: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className='w-3 h-3 mr-1' />,
      },
      failed: {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className='w-3 h-3 mr-1' />,
      },
      pending: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <Clock className='w-3 h-3 mr-1' />,
      },
      'in-progress': {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <RefreshCw className='w-3 h-3 mr-1' />,
      },
      active: {
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <Play className='w-3 h-3 mr-1' />,
      },
      draft: {
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: <Edit className='w-3 h-3 mr-1' />,
      },
      paused: {
        className: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <Pause className='w-3 h-3 mr-1' />,
      },
      training: {
        className: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: <BarChart3 className='w-3 h-3 mr-1' />,
      },
      deployed: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className='w-3 h-3 mr-1' />,
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Badge className={`flex items-center border ${config.className}`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  // Función para obtener colores de categorías
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      comunicacion: 'bg-blue-100 text-blue-800 border-blue-200',
      ventas: 'bg-green-100 text-green-800 border-green-200',
      analisis: 'bg-purple-100 text-purple-800 border-purple-200',
      general: 'bg-gray-100 text-gray-800 border-gray-200',
      soporte: 'bg-orange-100 text-orange-800 border-orange-200',
      marketing: 'bg-pink-100 text-pink-800 border-pink-200',
    };
    return colors[category.toLowerCase()] || colors['general'];
  };

  // Componente de error
  const ErrorAlert = ({
    error,
    onRetry,
  }: {
    error: any;
    onRetry: () => void;
  }) => (
    <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
      <div className='flex items-center'>
        <AlertCircle className='h-4 w-4 text-red-600 mr-2' />
        <span className='text-red-800'>
          {error?.message || 'Ha ocurrido un error'}
        </span>
        <Button
          variant='outline'
          size='sm'
          onClick={onRetry}
          className='ml-auto'
        >
          <RefreshCw className='h-3 w-3 mr-1' />
          Reintentar
        </Button>
      </div>
    </div>
  );

  return (
    <div className='space-y-6'>
      <div className='flex justify-between items-center'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>
            Modelos de Fine-tuning
          </h3>
          <p className='text-gray-600'>
            Modelos entrenados con datos de evaluaciones aprobadas
          </p>
        </div>
        <Button className='bg-blue-600 hover:bg-blue-700 text-white'>
          <Plus className='h-4 w-4 mr-2' />
          Nuevo Modelo
        </Button>
      </div>

      {/* Error handling */}
      {models.error && (
        <ErrorAlert error={models.error} onRetry={models.refreshModels} />
      )}

      {models.loading ? (
        <div className='flex justify-center items-center p-8'>
          <RefreshCw className='h-6 w-6 animate-spin text-blue-600' />
          <span className='ml-2 text-gray-600'>Cargando modelos...</span>
        </div>
      ) : (
        <div className='grid gap-4 lg:grid-cols-3'>
          {models.models.map(model => (
            <Card key={model.id} className='border-0 shadow-sm card-hover'>
              <CardContent className='p-4'>
                <div className='space-y-3'>
                  {/* Header compacto */}
                  <div className='flex items-start justify-between'>
                    <div className='flex-1 min-w-0'>
                      <h4 className='font-semibold text-gray-900 truncate'>
                        {model.name}
                      </h4>
                      <p className='text-sm text-gray-500 truncate'>
                        {model.type}
                      </p>
                    </div>
                    {getStatusBadge(model.status as StatusType)}
                  </div>

                  {/* Categorías reales basadas en tasks */}
                  <div className='flex flex-wrap gap-1'>
                    {/* Mostrar categorías únicas del modelo basadas en sus tasks */}
                    {model.categories && model.categories.length > 0 ? (
                      model.categories.map((category: string) => (
                        <Badge
                          key={category}
                          className={`text-xs px-2 py-1 border ${getCategoryColor(category)}`}
                        >
                          {category}
                        </Badge>
                      ))
                    ) : (
                      // Fallback: mostrar tipo de modelo si no hay categorías
                      <Badge
                        className={`text-xs px-2 py-1 border ${getCategoryColor(model.type)}`}
                      >
                        {model.type}
                      </Badge>
                    )}
                    {model.status === 'deployed' && (
                      <Badge className='text-xs px-2 py-1 border bg-blue-100 text-blue-800 border-blue-200'>
                        En producción
                      </Badge>
                    )}
                  </div>

                  {/* Métrica principal compacta */}
                  <div className='flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100'>
                    <div>
                      <p className='text-sm font-medium text-gray-600'>
                        Puntuación
                      </p>
                      <p className='text-xs text-gray-500'>Benchmarking</p>
                    </div>
                    <div className='text-2xl font-bold text-green-600'>
                      {model.accuracy}%
                    </div>
                  </div>

                  {/* Progreso de entrenamiento si aplica */}
                  {model.status === 'training' &&
                    model.progress !== undefined && (
                      <div className='space-y-2'>
                        <div className='flex justify-between text-sm'>
                          <span className='text-gray-600'>Progreso</span>
                          <span className='text-purple-600 font-medium'>
                            {model.progress}%
                          </span>
                        </div>
                        <Progress value={model.progress} className='h-2' />
                      </div>
                    )}

                  {/* Botones de acción compactos */}
                  <div className='flex gap-2 pt-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      className='flex-1'
                      onClick={() => setSelectedModel(model)}
                    >
                      <Eye className='h-3 w-3 mr-1' />
                      Detalles
                    </Button>
                    {model.status === 'completed' && (
                      <Button
                        size='sm'
                        className='bg-green-600 hover:bg-green-700 text-white'
                        onClick={() => models.deployModel(model.id)}
                        disabled={models.loading}
                      >
                        <Play className='h-3 w-3 mr-1' />
                        Desplegar
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {models.models.length === 0 && (
            <div className='col-span-3 text-center text-gray-500 py-8'>
              <Brain className='h-12 w-12 mx-auto mb-4 text-gray-300' />
              <p className='text-lg font-medium'>No hay modelos disponibles</p>
              <p className='text-sm'>Crea tu primer modelo para comenzar</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalle de Modelo */}
      <Dialog
        open={!!selectedModel}
        onOpenChange={open => !open && setSelectedModel(null)}
      >
        <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Brain className='h-5 w-5 text-blue-600' />
              Detalle del Modelo - {selectedModel?.name}
            </DialogTitle>
            <DialogDescription>
              Información completa y métricas de rendimiento
            </DialogDescription>
          </DialogHeader>

          {selectedModel && (
            <div className='space-y-6'>
              {/* Header con información principal */}
              <div className='grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg'>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Nombre del Modelo
                  </Label>
                  <p className='font-medium text-gray-900'>
                    {selectedModel.name}
                  </p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Tipo
                  </Label>
                  <p className='text-gray-900'>{selectedModel.type}</p>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Estado
                  </Label>
                  <div className='mt-1'>
                    {getStatusBadge(selectedModel.status as StatusType)}
                  </div>
                </div>
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Precisión
                  </Label>
                  <p className='text-2xl font-bold text-green-600'>
                    {selectedModel.accuracy}%
                  </p>
                </div>
              </div>

              {/* Tasks asociadas */}
              {selectedModel.tasks && selectedModel.tasks.length > 0 && (
                <div>
                  <Label className='text-sm font-semibold text-gray-700'>
                    Tasks Vinculadas ({selectedModel.tasks.length})
                  </Label>
                  <div className='mt-2 flex flex-wrap gap-2'>
                    {selectedModel.tasks.map((task: any) => (
                      <Badge
                        key={task.id}
                        className={`${getCategoryColor(task.category || 'general')}`}
                      >
                        {task.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
